import React from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  useTheme,
} from "@mui/material";
import { VoucherDetail } from "../../../../../models/Vouchers/VoucherDetail";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";

interface VoucherEmailersTableProp {
  maxHeight: number;
  voucherEmailers: VoucherDetail[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
}

const VoucherEmailersTable = ({
  maxHeight,
  voucherEmailers,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: VoucherEmailersTableProp) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const getStatusChip = (emailSent: boolean, emailError: string | null) => {
    if (emailError) {
      return (
        <Chip
          label="Error"
          size="small"
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    if (emailSent) {
      return (
        <Chip
          label="Sent"
          size="small"
          sx={{
            backgroundColor: theme.palette.success.main,
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      );
    }
    return (
      <Chip
        label="Pending"
        size="small"
        sx={{
          backgroundColor: theme.palette.grey[400],
          color: "#fff",
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      />
    );
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {voucherEmailers.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                      minWidth: "60px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Subject" fontSize="0.8rem" />
                  <AppTableHeaderCell
                    title="Recipient Email"
                    fontSize="0.8rem"
                  />
                  <AppTableHeaderCell
                    title="Recipient Name"
                    fontSize="0.8rem"
                  />
                  <AppTableHeaderCell title="Product Name" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Amount" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Status" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Sent At" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Sent By" fontSize="0.8rem" />
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "created_at"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "created_at",
                          sortBy === "created_at" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_at")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Created At
                    </TableSortLabel>
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {voucherEmailers.map((voucher, index) => (
                  <TableRow
                    key={voucher.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <AppTableRowCell
                      sx={{
                        textAlign: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 10,
                        boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                        backgroundColor:
                          index % 2 === 0
                            ? theme.palette.background.paper
                            : theme.palette.action.hover,
                        minWidth: "60px",
                      }}
                    >
                      {voucher.id}
                    </AppTableRowCell>
                    <AppTableRowCell
                      sx={{
                        minWidth: "300px",
                        maxWidth: "350px",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {voucher.subject}
                    </AppTableRowCell>
                    <AppTableRowCell>{voucher.email}</AppTableRowCell>
                    <AppTableRowCell>{voucher.name}</AppTableRowCell>
                    <AppTableRowCell
                      sx={{
                        minWidth: "300px",
                        maxWidth: "350px",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {voucher.product_name}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {voucher.currency_code} {voucher.amount}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {getStatusChip(voucher.email_sent, voucher.email_error)}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {voucher.email_sent_at
                        ? formatDate(voucher.email_sent_at)
                        : "-"}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {voucher.sent_by_admin_name}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {formatDate(voucher.created_at)}
                    </AppTableRowCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10rem"
          textAlign="center"
        >
          No voucher emailers found.
        </Box>
      )}
    </Paper>
  );
};

export default VoucherEmailersTable;
